import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Group, Image, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { CowDiagram } from './CowDiagram';
import { GuideHit } from '../../utils/guideMatcher';
import {
  ANIMALS, animalOf, AXES, Cut, CUT_ITEMS, CutItem, eatenAsMeat, Ingredient, INGREDIENTS, MenuWord, Offal, areaRatings, Region, regionsOfWord,
} from '../../utils/guide';
import guide from './GuideSheet.module.css';

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** n filled; lightly up to upTo, where some of an area's cuts rate higher */
const Pips: React.FC<{ n: number; upTo?: number }> = ({ n, upTo = n }) => (
  <span className={guide.pips} role="img" aria-label={upTo > n ? `About ${n} out of 5, up to ${upTo} for some cuts` : `${n} out of 5`}>
    {[1, 2, 3, 4, 5].map((p) => <span key={p} className={`${guide.pip} ${p <= n ? guide.pipOn : p <= upTo ? guide.pipMaybe : ''}`} />)}
  </span>
);

/** Ratings by axis index; axes left out of `show` aren't listed */
const Scores: React.FC<{ n: number[]; upTo?: number[]; show?: number[] }> = ({ n, upTo, show }) => (
  <dl className={guide.scores}>
    {AXES.map(([en, ja], k) => (!show || show.includes(k)) && (
      <React.Fragment key={en}>
        <dt>{en}<span lang="ja">{ja}</span></dt>
        <dd><Pips n={n[k]} upTo={upTo?.[k]} /></dd>
      </React.Fragment>
    ))}
  </dl>
);

// Rarity is how little of one cut a cow gives: it says nothing about a whole section or a word like ロース
const AREA_AXES = AXES.map((_, k) => k).filter((k) => AXES[k][0] !== 'Rarity');

/** How a section's or a menu word's cuts rate as a whole: typical, and how high some go. */
const AreaRatings: React.FC<{ regions: number[]; label: React.ReactNode }> = ({ regions, label }) => {
  const r = areaRatings(regions);
  if (!r) return null;
  const spread = AREA_AXES.some((k) => r.top[k] > r.typical[k]);
  return (
    <>
      <Scores n={r.typical} upTo={r.top} show={AREA_AXES} />
      <Text size="xs" c="dimmed">
        Typical of the {r.cuts} cuts in {label}{spread ? '; light dots: some cuts rate higher' : ''}. Ratings: TOMMY BEEF, for Shiroishi beef.
      </Text>
    </>
  );
};

const listJa = (regions: number[]) => {
  const names = regions.map((n) => CUT_ITEMS.get(`r${n}`)!.ja);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
};

const Title: React.FC<{ ja: string; ro?: string; en?: string; gloss?: string; no?: number | string }> = ({ ja, ro, en, gloss, no }) => (
  <Group gap="sm" wrap="nowrap" align="flex-start">
    {no != null && <Text className={guide.no} aria-hidden="true">{typeof no === 'number' ? String(no).padStart(2, '0') : no}</Text>}
    <Box>
      <Text className={guide.ja} lang="ja">{ja}</Text>
      {(ro || en) && <Text size="sm" c="dimmed">{[ro && <b key="r">{ro}</b>, en].filter(Boolean).reduce<React.ReactNode[]>((a, x, i) => (i ? [...a, ' · ', x] : [x]), [])}</Text>}
      {gloss && <Text size="xs" c="dimmed" fs="italic">{gloss}</Text>}
    </Box>
  </Group>
);

const Row: React.FC<{ item: CutItem; onPick: (key: string) => void }> = ({ item, onPick }) => (
  <li>
    <UnstyledButton className={guide.row} onClick={() => onPick(item.key)}>
      <span className={guide.rowMain}>
        <Text component="span" fw={600} lang="ja">{item.ja}</Text>
        <Text component="span" size="xs" c="dimmed">{item.ro} · {item.en}</Text>
      </span>
      {item.kind === 'cut' && <Text component="span" size="xs" c="dimmed">{String(item.no).padStart(2, '0')}</Text>}
      <IconChevronRight size={16} />
    </UnstyledButton>
  </li>
);

const CutView: React.FC<{ item: CutItem; onPick: (key: string) => void }> = ({ item, onPick }) => {
  if (item.kind === 'cut') {
    const region = CUT_ITEMS.get(`r${(item as Cut).r}`) as Region;
    return (
      <Stack gap="sm">
        <CowDiagram item={item} />
        <Title no={item.no} ja={item.ja} ro={item.ro} en={item.en} gloss={item.gloss} />
        <Text size="sm" c="dimmed">From section {region.n}, <span lang="ja">{region.ja}</span> · {region.en}</Text>
        <Scores n={item.s} />
        <Text size="xs" c="dimmed">Cut {item.no} of 45. Ratings and note: TOMMY BEEF, for Shiroishi beef.</Text>
      </Stack>
    );
  }
  if (item.kind === 'offal') {
    const o = item as Offal;
    return (
      <Stack gap="sm">
        <CowDiagram item={item} />
        <Title ja={o.ja} ro={o.ro} en={o.en} gloss={o.gloss} />
        {eatenAsMeat(o) ? (
          <>
            <Text size="sm" c="dimmed">Classed as offal (ホルモン), but eaten as meat at yakiniku</Text>
            <Scores n={o.s} />
          </>
        ) : (
          <Text size="sm" c="dimmed">Offal (ホルモン) · <span lang="ja">{o.group?.ja}</span> · {o.group?.en}</Text>
        )}
        {o.safety && <Text size="sm" className={guide.safety}>{o.safety}</Text>}
        <Text size="xs" c="dimmed">
          {eatenAsMeat(o) && 'Ratings: our estimate (TOMMY BEEF doesn’t rate offal). '}
          Note: {['sekizui', 'nekutai'].includes(o.id) ? 'iso.labo' : 'はじめの肉屋'}.
        </Text>
      </Stack>
    );
  }
  if (item.kind === 'region') {
    const r = item as Region;
    return (
      <Stack gap="sm">
        <CowDiagram item={item} />
        <Title no={r.n} ja={r.ja} ro={r.ro} en={r.en} gloss={r.gloss} />
        <Text size="sm">{r.sum}</Text>
        <AreaRatings regions={[r.n]} label="this section" />
      </Stack>
    );
  }
  const w = item as MenuWord;
  // ホルモン is any organ: list them (not タン or ハラミ, which count as meat). The others name an area of the cow.
  if (w.id === 'horumon') {
    const offal = [...CUT_ITEMS.values()].filter((i) => i.kind === 'offal' && !eatenAsMeat(i));
    return (
      <Stack gap="sm">
        <Title ja={w.ja} ro={w.ro} en={w.en} />
        <Text fw={700}>Offal in the guide</Text>
        <ul className={guide.rows}>{offal.map((i) => <Row key={i.key} item={i} onPick={onPick} />)}</ul>
      </Stack>
    );
  }
  const regions = regionsOfWord(w);
  return (
    <Stack gap="sm">
      {regions.length > 0 && <CowDiagram item={item} lit={regions} />}
      <Title ja={w.ja} ro={w.ro} en={w.en} />
      <Text size="sm" dangerouslySetInnerHTML={{ __html: w.html }} />
      {/* 赤身 describes the meat, not a place: ratings across every lean section would say nothing */}
      {w.id !== 'akami' && <AreaRatings regions={regions} label={<span lang="ja">{listJa(regions)}</span>} />}
    </Stack>
  );
};

/** What the item view doesn't already show: kana, kind and Japan's season. */
const IngredientView: React.FC<{ item: Ingredient }> = ({ item }) => {
  const now = new Date().getMonth() + 1;
  return (
    <Stack gap="sm">
      {/* The item view above is the dish; this is the ingredient in it, with its photo */}
      <Group gap="sm" wrap="nowrap" align="center">
        {item.img && <Image src={`/scan-ingredients/${encodeURIComponent(item.img)}`} alt={item.en} w={64} h={64} radius="md" fit="cover" loading="lazy" className={guide.thumb} />}
        <Stack gap={4}>
          <Text fw={600} tt="capitalize">{item.en}</Text>
          <Group gap={6}>
            <Text size="sm" c="dimmed" lang="ja">{item.kana}</Text>
            <Badge color="green" variant="light" tt="capitalize">{item.kind}</Badge>
            {item.sub && <Badge color="gray" variant="light" tt="capitalize">{item.sub}</Badge>}
          </Group>
        </Stack>
      </Group>
      {item.months.length > 0 && (
        <Box>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>In season in Japan</Text>
          <div className={guide.months} role="img" aria-label={`In season: ${item.months.map((m) => MONTH_NAMES[m - 1]).join(', ')}`}>
            {MONTHS.map((m, i) => (
              <span key={i} className={`${guide.month} ${item.months.includes(i + 1) ? guide.monthOn : ''} ${i + 1 === now ? guide.monthNow : ''}`}>{m}</span>
            ))}
          </div>
          {item.months.includes(now) && <Text size="xs" mt={4}>In season now.</Text>}
        </Box>
      )}
      {item.note && <Text size="sm" c="dimmed" lang="ja">{item.note}</Text>}
    </Stack>
  );
};

interface GuideExtrasProps {
  /** The line as printed, and the guide names found in it */
  text: string;
  hits: GuideHit[];
  /** The whole menu is about another animal (cuts only) */
  menuAnimal: string | null;
  /** Changes with the tapped line; the view resets */
  resetKey: number;
}

/**
 * What the guides add to the item view for a line: the cow with the cut lit,
 * ratings, season, notes. A tab per name when the line names several things.
 * Shown under MenuItemDetails in the scan sheet.
 */
export const GuideExtras: React.FC<GuideExtrasProps> = ({ text, hits, menuAnimal, resetKey }) => {
  const [tab, setTab] = useState(0);
  const [sub, setSub] = useState<string | null>(null);

  useEffect(() => {
    setTab(0);
    setSub(null);
  }, [resetKey]);

  const keys = [...new Set(hits.flatMap((h) => h.keys))];
  const key = sub || keys[Math.min(tab, keys.length - 1)];
  const cut = key ? CUT_ITEMS.get(key) : undefined;
  const ingredient = key ? INGREDIENTS.get(key) : undefined;
  // Cuts are tabbed by their Japanese name, ingredients by their English one
  const tabLabel = (k: string) => CUT_ITEMS.get(k)?.ja || INGREDIENTS.get(k)?.en || k;

  const lineAnimal = animalOf(text);
  const other = lineAnimal && lineAnimal !== 'beef' ? lineAnimal : !lineAnimal ? menuAnimal : null;
  const animal = other ? ANIMALS.find(([k]) => k === other) : null;

  return (
    <Box>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>From the guide</Text>
      {keys.length > 1 && (
        <Group gap={6} my="xs" role="tablist">
          {keys.map((k, n) => (
            <Button
              key={k}
              size="compact-sm"
              radius="xl"
              variant={n === tab && !sub ? 'filled' : 'light'}
              color={CUT_ITEMS.has(k) ? 'orange' : 'green'}
              role="tab"
              aria-selected={n === tab && !sub}
              onClick={() => { setTab(n); setSub(null); }}
            >
              <span lang={CUT_ITEMS.has(k) ? 'ja' : 'en'}>{tabLabel(k)}</span>
            </Button>
          ))}
        </Group>
      )}
      {sub && (
        <Button variant="subtle" px={0} size="compact-sm" leftSection={<IconChevronLeft size={16} />} onClick={() => setSub(null)}>
          Back to <span lang="ja">{CUT_ITEMS.get(keys[tab])?.ja}</span>
        </Button>
      )}
      <Box mt="xs">
        {cut && <CutView item={cut} onPick={setSub} />}
        {ingredient && <IngredientView item={ingredient} />}
        {animal && cut && cut.kind !== 'word' && (
          <Text size="sm" mt="sm" className={guide.safety}>
            {lineAnimal ? 'This line mentions' : 'This menu mentions'} {animal[1]}. The name means the same part, but these notes and ratings are for beef.
          </Text>
        )}
      </Box>
    </Box>
  );
};
